import React, {
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";

import { QRCodeSVG } from "qrcode.react";
import html2canvas from "html2canvas";

import SIGN_TEMPLATES from "./SignTemplates";

const COW_BROWN = "#6A4026";

const formatEventDate = (value) => {
  if (!value) return "";

  try {
    const raw =
      value?.toDate && typeof value.toDate === "function"
        ? value.toDate()
        : value;

    let date;

    if (
      typeof raw === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(raw)
    ) {
      const [year, month, day] = raw.split("-").map(Number);
      date = new Date(year, month - 1, day);
    } else {
      date = new Date(raw);
    }

    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const SignCreator = ({
  open,
  onClose,
  event,
  eventUrl,
}) => {
  const [selectedIndex, setSelectedIndex] =
    useState(0);

  const [saving, setSaving] =
    useState(false);

  const exportRef = useRef(null);
  const touchStartXRef = useRef(null);

  const [dragX, setDragX] =
    useState(0);

  const [isDragging, setIsDragging] =
    useState(false);

  const template =
    SIGN_TEMPLATES[selectedIndex];

  const eventName =
    event?.details?.eventName ||
    event?.eventName ||
    "Your Event";

  const eventDate =
    event?.eventDate ||
    event?.details?.eventDate ||
    null;

  const formattedDate = useMemo(
    () => formatEventDate(eventDate),
    [eventDate]
  );

  const previousTemplate = () => {
    setSelectedIndex((current) =>
      current === 0
        ? SIGN_TEMPLATES.length - 1
        : current - 1
    );
  };

  const nextTemplate = () => {
    setSelectedIndex((current) =>
      current ===
      SIGN_TEMPLATES.length - 1
        ? 0
        : current + 1
    );
  };

  const handleTouchStart = (e) => {
    const x =
      e.touches[0]?.clientX;

    if (x == null) return;

    touchStartXRef.current = x;
    setIsDragging(true);
    setDragX(0);
  };

  const handleTouchMove = (e) => {
    if (
      touchStartXRef.current == null
    ) {
      return;
    }

    const currentX =
      e.touches[0]?.clientX;

    if (currentX == null) return;

    const distance =
      currentX -
      touchStartXRef.current;

    const resistedDistance =
      distance * 0.85;

    setDragX(resistedDistance);
  };

  const handleTouchEnd = () => {
    if (
      touchStartXRef.current == null
    ) {
      return;
    }

    const threshold = 65;

    setIsDragging(false);
    touchStartXRef.current = null;

    if (dragX <= -threshold) {
      setDragX(-450);

      window.setTimeout(() => {
        nextTemplate();

        setDragX(450);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setDragX(0);
          });
        });
      }, 180);

      return;
    }

    if (dragX >= threshold) {
      setDragX(450);

      window.setTimeout(() => {
        previousTemplate();

        setDragX(-450);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setDragX(0);
          });
        });
      }, 180);

      return;
    }

    setDragX(0);
  };

  const saveSign = async () => {
    if (
      !exportRef.current ||
      !eventUrl ||
      saving
    ) {
      return;
    }

    try {
      setSaving(true);

      if (document.fonts?.ready) {
        await document.fonts.ready;
      }

      await new Promise(
        (resolve) => {
          requestAnimationFrame(() => {
            requestAnimationFrame(
              resolve
            );
          });
        }
      );

      const canvas =
        await html2canvas(
          exportRef.current,
          {
            backgroundColor:
              template.background,
            scale: 1,
            useCORS: true,
            logging: false,
          }
        );

      const blob =
        await new Promise(
          (resolve) => {
            canvas.toBlob(
              resolve,
              "image/png",
              1
            );
          }
        );

      if (!blob) {
        throw new Error(
          "Could not create sign image"
        );
      }

      const safeName =
        eventName
          .replace(
            /[^a-z0-9]+/gi,
            "-"
          )
          .replace(/^-|-$/g, "")
          .toLowerCase();

      const fileName = `${
        safeName || "event"
      }-qr-sign.png`;

      const file =
        new File(
          [blob],
          fileName,
          {
            type: "image/png",
          }
        );

      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare({
          files: [file],
        })
      ) {
        await navigator.share({
          files: [file],
          title: `${eventName} QR Sign`,
        });

        return;
      }

      const objectUrl =
        URL.createObjectURL(blob);

      const link =
        document.createElement("a");

      link.href = objectUrl;
      link.download = fileName;

      document.body.appendChild(
        link
      );

      link.click();
      link.remove();

      window.setTimeout(() => {
        URL.revokeObjectURL(
          objectUrl
        );
      }, 1000);
    } catch (error) {
      if (
        error?.name !==
        "AbortError"
      ) {
        console.error(
          "Failed to save QR sign:",
          error
        );

        alert(
          "We couldn't save your sign. Please try again."
        );
      }
    } finally {
      setSaving(false);
    }
  };

  if (!template) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          background:
            "linear-gradient(180deg, #202124 0%, #121212 100%)",
        },
      }}
    >
      {/* HEADER */}

      <Box
        sx={{
          height: 72,
          px: 2,
          display: "grid",
          gridTemplateColumns:
            "48px 1fr 48px",
          alignItems: "center",
          borderBottom:
            "1px solid rgba(255,255,255,0.08)",
          flexShrink: 0,
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label="Close sign creator"
          sx={{
            color: "#fff",
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              color: "#fff",
              fontSize: 16,
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {template.name}
          </Typography>

          <Typography
            sx={{
              color:
                "rgba(255,255,255,0.55)",
              fontSize: 12,
              mt: 0.25,
            }}
          >
            Swipe to change style
          </Typography>
        </Box>

        <Box />
      </Box>

      {/* PREVIEW AREA */}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 2,
          py: 2.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: {
              xs: 0.5,
              sm: 1.5,
            },
          }}
        >
          <IconButton
            onClick={
              previousTemplate
            }
            aria-label="Previous template"
            sx={{
              color: "#fff",
              flexShrink: 0,
              bgcolor:
                "rgba(255,255,255,0.08)",

              "&:hover": {
                bgcolor:
                  "rgba(255,255,255,0.13)",
              },
            }}
          >
            <ArrowBackIosNewIcon
              sx={{
                fontSize: 18,
              }}
            />
          </IconButton>

          {/* SIGN PREVIEW */}

          <Box
            onTouchStart={
              handleTouchStart
            }
            onTouchMove={
              handleTouchMove
            }
            onTouchEnd={
              handleTouchEnd
            }
            onTouchCancel={
              handleTouchEnd
            }
            sx={{
              width:
                "min(72vw, 390px)",
              aspectRatio: "5 / 7",
              borderRadius:
                "22px",
              overflow: "hidden",
              boxShadow:
                "0 22px 60px rgba(0,0,0,0.35)",
              flexShrink: 0,

              transform: `translateX(${dragX}px) scale(${
                isDragging
                  ? 0.985
                  : 1
              })`,

              transition:
                isDragging
                  ? "none"
                  : "transform 180ms cubic-bezier(0.22, 1, 0.36, 1)",

              touchAction: "pan-y",

              cursor:
                isDragging
                  ? "grabbing"
                  : "grab",

              userSelect: "none",
              WebkitUserSelect:
                "none",

              willChange:
                "transform",
            }}
          >
            <SignArtwork
              template={template}
              eventName={
                eventName
              }
              formattedDate={
                formattedDate
              }
              eventUrl={
                eventUrl
              }
              preview
            />
          </Box>

          <IconButton
            onClick={
              nextTemplate
            }
            aria-label="Next template"
            sx={{
              color: "#fff",
              flexShrink: 0,
              bgcolor:
                "rgba(255,255,255,0.08)",

              "&:hover": {
                bgcolor:
                  "rgba(255,255,255,0.13)",
              },
            }}
          >
            <ArrowForwardIosIcon
              sx={{
                fontSize: 18,
              }}
            />
          </IconButton>
        </Box>

        {/* TEMPLATE DOTS */}

        <Box
          sx={{
            mt: 2,
            display: "flex",
            gap: 0.8,
            justifyContent:
              "center",
          }}
        >
          {SIGN_TEMPLATES.map(
            (item, index) => (
              <Box
                key={item.id}
                onClick={() =>
                  setSelectedIndex(
                    index
                  )
                }
                sx={{
                  width:
                    selectedIndex ===
                    index
                      ? 9
                      : 7,

                  height:
                    selectedIndex ===
                    index
                      ? 9
                      : 7,

                  borderRadius:
                    "50%",

                  cursor:
                    "pointer",

                  bgcolor:
                    selectedIndex ===
                    index
                      ? "#fff"
                      : "rgba(255,255,255,0.3)",

                  transition:
                    "all 150ms ease",
                }}
              />
            )
          )}
        </Box>

        {/* SAVE BUTTON */}

        <Button
          fullWidth
          variant="contained"
          startIcon={
            <DownloadRoundedIcon />
          }
          disabled={
            saving || !eventUrl
          }
          onClick={saveSign}
          sx={{
            mt: 2.5,
            mb: 2,
            maxWidth: 430,
            py: 1.45,
            borderRadius:
              "999px",
            bgcolor: "#fff",
            color: "#111",
            fontWeight: 700,
            fontSize: 16,
            textTransform: "none",
            boxShadow: "none",

            "&:hover": {
              bgcolor: "#f4f4f4",
              boxShadow: "none",
            },

            "&.Mui-disabled": {
              bgcolor:
                "rgba(255,255,255,0.25)",

              color:
                "rgba(255,255,255,0.45)",
            },
          }}
        >
          {saving
            ? "Creating Sign…"
            : "Save Sign"}
        </Button>

        <Typography
          sx={{
            color:
              "rgba(255,255,255,0.48)",
            fontSize: 12,
            textAlign: "center",
            mb: 2,
          }}
        >
          High-resolution image ready
          to print or share
        </Typography>
      </Box>

      {/* HIGH RES EXPORT */}

      <Box
        sx={{
          position: "fixed",
          left: "-10000px",
          top: 0,
          width: "1500px",
          height: "2100px",
          pointerEvents: "none",
        }}
      >
        <Box
          ref={exportRef}
          sx={{
            width: "1500px",
            height: "2100px",
          }}
        >
          <SignArtwork
            template={template}
            eventName={
              eventName
            }
            formattedDate={
              formattedDate
            }
            eventUrl={
              eventUrl
            }
            preview={false}
          />
        </Box>
      </Box>
    </Dialog>
  );
};

const SignArtwork = ({
  template,
  eventName,
  formattedDate,
  eventUrl,
  preview,
}) => {
  const isDark =
    Boolean(template.dark);

  const isCowPrint =
    template.id === "cowprint";

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        bgcolor:
          template.background,
        color:
          template.textColor,
        position: "relative",
        overflow: "hidden",
        fontFamily:
          template.fontFamily,
      }}
    >
      {/* COW PRINT */}

      {isCowPrint && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          <svg
            viewBox="0 0 1000 1400"
            preserveAspectRatio="none"
            width="100%"
            height="100%"
            style={{
              position: "absolute",
              inset: 0,
              display: "block",
            }}
          >
            {/* TOP LEFT */}

            <path
              fill={COW_BROWN}
              d="
                M0 0
                H268
                C276 34 265 69 246 95
                C226 122 235 150 211 175
                C188 199 157 190 132 213
                C103 239 76 227 53 249
                C35 267 17 272 0 268
                Z
              "
            />

            {/* TOP RIGHT */}

            <path
              fill={COW_BROWN}
              d="
                M734 0
                H1000
                V246
                C973 239 950 216 920 209
                C889 202 866 221 840 200
                C815 180 821 151 795 130
                C770 109 749 99 745 70
                C741 44 750 22 734 0
                Z
              "
            />

            {/* UPPER LEFT SMALL */}

            <path
              fill={COW_BROWN}
              d="
                M111 300
                C130 281 159 280 177 298
                C195 315 191 340 178 358
                C166 374 150 381 132 370
                C114 360 104 346 105 329
                C106 316 105 307 111 300
                Z
              "
            />

            {/* LEFT CENTER */}

            <path
              fill={COW_BROWN}
              d="
                M0 433
                C31 419 61 424 82 441
                C105 459 106 483 96 506
                C88 527 102 545 108 567
                C114 591 101 618 79 634
                C54 652 28 644 0 657
                Z
              "
            />

            {/* RIGHT CENTER */}

            <path
              fill={COW_BROWN}
              d="
                M1000 415
                C970 402 941 407 920 428
                C900 449 905 473 893 497
                C881 521 889 549 910 567
                C933 586 962 578 1000 591
                Z
              "
            />

            {/* LOWER LEFT ORGANIC */}

            <path
              fill={COW_BROWN}
              d="
                M123 863
                C143 843 174 844 190 865
                C203 883 196 903 204 921
                C213 942 199 965 179 975
                C156 986 127 976 117 952
                C108 931 112 909 105 892
                C101 880 110 871 123 863
                Z
              "
            />

            {/* LOWER RIGHT ORGANIC */}

            <path
              fill={COW_BROWN}
              d="
                M843 842
                C863 822 894 825 910 845
                C925 863 918 883 930 901
                C944 922 934 948 913 961
                C891 975 861 969 848 947
                C835 927 838 906 829 889
                C822 873 831 854 843 842
                Z
              "
            />

            {/* BOTTOM LEFT */}

            <path
              fill={COW_BROWN}
              d="
                M0 1110
                C34 1094 65 1099 88 1119
                C111 1139 108 1166 126 1188
                C145 1212 171 1208 193 1228
                C215 1249 219 1278 202 1300
                C183 1325 157 1322 134 1338
                C105 1357 90 1382 79 1400
                H0
                Z
              "
            />

            {/* BOTTOM RIGHT */}

            <path
              fill={COW_BROWN}
              d="
                M1000 1092
                C968 1082 938 1091 918 1113
                C895 1138 901 1164 881 1187
                C862 1210 836 1208 815 1227
                C791 1249 790 1279 808 1301
                C827 1325 853 1323 875 1339
                C902 1358 916 1382 928 1400
                H1000
                Z
              "
            />
          </svg>
        </Box>
      )}

      {/* CONTENT */}

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 1,

          display: "flex",
          flexDirection: "column",
          alignItems: "center",

          textAlign: "center",

          px: preview
            ? 4
            : "150px",

          pt: preview
            ? 5
            : "180px",

          pb: preview
            ? 4
            : "150px",
        }}
      >
        {/* EYEBROW */}

        <Typography
          component="div"
          sx={{
            color:
              template.textColor,

            opacity:
              isCowPrint
                ? 0.78
                : 0.7,

            fontFamily:
              "Arial, Helvetica, sans-serif",

            fontWeight: 700,

            letterSpacing:
              preview
                ? "1.5px"
                : "7px",

            fontSize:
              preview
                ? 9
                : "34px",

            lineHeight: 1.2,
          }}
        >
          {template.eyebrow}
        </Typography>

        {/* EVENT NAME */}

        <Typography
          component="div"
          sx={{
            mt: preview
              ? 1.2
              : "42px",

            color:
              template.textColor,

            fontFamily:
              template.headlineFontFamily,

            fontWeight:
              template.id ===
                "editorial" ||
              template.id ===
                "romantic" ||
              template.id ===
                "minimal" ||
              isCowPrint
                ? 500
                : 800,

            fontSize:
              preview
                ? 25
                : "96px",

            letterSpacing:
              preview
                ? "-0.5px"
                : "-3px",

            lineHeight: 1.03,

            maxWidth: "100%",

            overflowWrap:
              "anywhere",
          }}
        >
          {eventName}
        </Typography>

        {/* DATE */}

        {formattedDate && (
          <Typography
            component="div"
            sx={{
              mt: preview
                ? 0.8
                : "28px",

              color:
                template.textColor,

              opacity:
                isCowPrint
                  ? 0.72
                  : 0.68,

              fontFamily:
                "Arial, Helvetica, sans-serif",

              fontWeight:
                isCowPrint
                  ? 600
                  : 500,

              fontSize:
                preview
                  ? 10
                  : "38px",

              letterSpacing:
                preview
                  ? "0.4px"
                  : "2px",
            }}
          >
            {formattedDate}
          </Typography>
        )}

        {/* DIVIDER */}

        {isCowPrint ? (
          <Box
            sx={{
              mt: preview
                ? 2.5
                : "95px",

              mb: preview
                ? 2.3
                : "85px",

              display: "flex",
              alignItems: "center",
              justifyContent:
                "center",

              gap: preview
                ? 1
                : "28px",

              width: "100%",
            }}
          >
            <Box
              sx={{
                width:
                  preview
                    ? 32
                    : "125px",

                height:
                  preview
                    ? 1.5
                    : "6px",

                bgcolor:
                  COW_BROWN,

                borderRadius: 999,
              }}
            />

            <Box
              component="span"
              sx={{
                color:
                  COW_BROWN,

                fontSize:
                  preview
                    ? 11
                    : "42px",

                lineHeight: 1,
              }}
            >
              ★
            </Box>

            <Box
              sx={{
                width:
                  preview
                    ? 32
                    : "125px",

                height:
                  preview
                    ? 1.5
                    : "6px",

                bgcolor:
                  COW_BROWN,

                borderRadius: 999,
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              width:
                preview
                  ? 42
                  : "165px",

              height:
                preview
                  ? 2
                  : "7px",

              borderRadius: 999,

              bgcolor:
                template.accentColor,

              opacity:
                isDark
                  ? 0.8
                  : 1,

              mt: preview
                ? 2.5
                : "95px",

              mb: preview
                ? 2.3
                : "85px",
            }}
          />
        )}

        {/* HEADLINE */}

        <Typography
          component="div"
          sx={{
            whiteSpace:
              "pre-line",

            color:
              template.textColor,

            fontFamily:
              template.headlineFontFamily,

            fontWeight:
              template.id ===
                "romantic" ||
              template.id ===
                "editorial" ||
              isCowPrint
                ? 500
                : 800,

            fontSize:
              preview
                ? 19
                : "76px",

            lineHeight: 1.05,

            letterSpacing:
              preview
                ? "-0.3px"
                : "-2px",
          }}
        >
          {template.headline}
        </Typography>

        {/* QR */}

        <Box
          sx={{
            mt: preview
              ? 2.6
              : "105px",

            bgcolor: "#fff",

            border:
              isCowPrint
                ? preview
                  ? `3px solid ${COW_BROWN}`
                  : `12px solid ${COW_BROWN}`
                : "none",

            borderRadius:
              preview
                ? 2
                : "38px",

            p: preview
              ? 1.3
              : "48px",

            display: "grid",

            placeItems: "center",

            boxShadow:
              isDark
                ? "0 12px 50px rgba(0,0,0,0.25)"
                : isCowPrint
                ? "0 10px 32px rgba(74,44,26,0.10)"
                : "0 10px 35px rgba(0,0,0,0.06)",
          }}
        >
          <QRCodeSVG
            value={
              eventUrl || ""
            }
            size={
              preview
                ? 150
                : 620
            }
            bgColor="#FFFFFF"
            fgColor="#000000"
            level="H"
            includeMargin
          />
        </Box>

        {/* INSTRUCTION */}

        <Typography
          component="div"
          sx={{
            mt: preview
              ? 2
              : "72px",

            color:
              template.textColor,

            fontFamily:
              "Arial, Helvetica, sans-serif",

            fontWeight: 700,

            fontSize:
              preview
                ? 11
                : "43px",

            lineHeight: 1.25,

            letterSpacing:
              isCowPrint
                ? preview
                  ? "0.1px"
                  : "1px"
                : "normal",
          }}
        >
          {template.instruction}
        </Typography>

        {/* BRAND */}

        <Typography
          component="div"
          sx={{
            mt: "auto",

            color:
              template.textColor,

            opacity:
              isCowPrint
                ? 0.48
                : 0.38,

            fontFamily:
              "Arial, Helvetica, sans-serif",

            fontWeight: 600,

            letterSpacing:
              preview
                ? "0.5px"
                : "3px",

            fontSize:
              preview
                ? 7
                : "25px",
          }}
        >
          ELEVATE 
        </Typography>
      </Box>
    </Box>
  );
};

export default SignCreator;